/*
  The category rule (§5, D2, D3, D16).

  Applied identically to the featured and the A-Z sections, and deliberately identical
  to admin's AlphabeticalGrid.tsx:140-148, so pop-report and admin can never disagree
  about where a game belongs:

      category = game.category?.trim()

      Sports   -> category === "Sports"
      TCG      -> category === "TCG" || !category
      excluded -> category === "Coins"

  Uncategorised games fall through to TCG. That is a data gap HQ is working through
  (D12, §5.3), not a display bug, and it is not corrected here.
*/

export type HomeCategory = "Sports" | "TCG";

/*
  New Zealand Mint, the one Coins game (§5.2). Referenced from three places once their
  tasks land: useGetGameOptions, useGetSearchOptions and the home pipeline.

  The home sections match this code as well as the category, belt and braces: the
  category rule already drops the game while category === "Coins" holds, but that is HQ
  data which D12 says HQ is actively changing, and a blanked category would put the game
  back on the public home page through the uncategorised-to-TCG fallback.
*/
export const MINT_GAME_CODE = "mint";

/* Returns null for a game that belongs to neither section — today, only Coins. */
export const resolveGameCategory = (
  category: string | null | undefined
): HomeCategory | null => {
  const trimmed = (category ?? "").trim();
  if (trimmed === "Sports") {
    return "Sports";
  }
  if (trimmed === "TCG" || trimmed === "") {
    return "TCG";
  }
  return null;
};

export const isInHomeCategory = (
  category: string | null | undefined,
  section: HomeCategory
): boolean => resolveGameCategory(category) === section;

/*
  The v1 surfaces (nav menu, advanced-search dropdown) have no category field at all
  (types/gameInfo.ts), so they exclude the game by code alone.
*/
export const isMintGameCode = (gameCode: string | null | undefined): boolean =>
  (gameCode ?? "").trim().toLowerCase() === MINT_GAME_CODE;

/*
  The visitor's TCG / Sports choice, remembered between visits (docs/specs/pop-report-batch3.md
  D1-D3). One choice drives the header's Games menu and the home page's Browse by Game and
  Top 10 alike. TCG until the visitor picks otherwise.

  Wrapped like the language toggle's saveBucket: storage can be blocked (private windows,
  locked-down browsers), and then the choice simply lasts for this visit.
*/
export const CATEGORY_STORAGE_KEY = "pcg.pop.category";
export const DEFAULT_HOME_CATEGORY: HomeCategory = "TCG";

export const isHomeCategory = (value: unknown): value is HomeCategory =>
  value === "TCG" || value === "Sports";

export const readSavedCategory = (): HomeCategory => {
  try {
    const stored = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
    return isHomeCategory(stored) ? stored : DEFAULT_HOME_CATEGORY;
  } catch (e) {
    return DEFAULT_HOME_CATEGORY;
  }
};

/* Called from the visitor's own click and from nowhere else. */
export const saveCategory = (category: HomeCategory): void => {
  try {
    window.localStorage.setItem(CATEGORY_STORAGE_KEY, category);
  } catch (e) {
    // Storage unavailable: the choice still holds for this visit.
  }
};
